import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  ListItemIcon,
  CircularProgress
} from '@mui/material';
import {
  Description as DocumentIcon,
  PictureAsPdf as PdfIcon,
  Image as ImageIcon,
  InsertDriveFile as FileIcon,
  Delete as DeleteIcon,
  Download as DownloadIcon,
  Visibility as VisibilityIcon
} from '@mui/icons-material';
import toast from 'react-hot-toast';
import { formatDate } from '../../utils/dateUtils';
import { Document } from '../../types/document.types';
import axios from '../../api/axiosConfig';

interface DocumentListProps {
  clientId: string;
  propertyId?: string;
  documentType: 'client' | 'property';
  documents: Document[];
  onDocumentDeleted?: () => void;
  onViewDocument?: (document: Document) => void;
  onDeleteDocument?: (documentId: string) => void;
  onFilteredCountChange?: (count: number) => void;
  loading?: boolean;
}

const getFileIcon = (type: string) => {
  if (type.startsWith('image/')) {
    return <ImageIcon />;
  } else if (type === 'application/pdf') {
    return <PdfIcon />;
  } else if (type === 'application/msword' || type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document') {
    return <DocumentIcon />;
  }
  return <FileIcon />;
};

const formatFileSize = (bytes: number) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

const DocumentList: React.FC<DocumentListProps> = ({
  clientId,
  propertyId,
  documentType,
  documents,
  onDocumentDeleted,
  onViewDocument,
  onDeleteDocument,
  onFilteredCountChange,
  loading
}) => {
  const [localLoading, setLocalLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedDocument, setSelectedDocument] = useState<Document | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [filteredDocuments, setFilteredDocuments] = useState<Document[]>([]);

  // Update filtered document count and notify parent when filtered documents change
  useEffect(() => {
    if (onFilteredCountChange) {
      onFilteredCountChange(filteredDocuments.length);
    }
  }, [filteredDocuments.length, onFilteredCountChange]);

  // Filter documents and update counts
  useEffect(() => {
    // Handle empty documents array
    if (!documents || documents.length === 0) {
      setFilteredDocuments([]);
      return;
    }
    
    // Log all documents for debugging
    console.log('All documents:', documents);
    
    // Filter out draft and hidden documents on the client side
    const filtered = documents.filter(doc => {
      // First check if document has metadata and is hidden or a draft
      // This is the most important filter to apply to ALL documents
      if (doc.metadata) {
        // Explicitly check for hidden flag
        if (doc.metadata.hidden === true) {
          console.log('Filtering out hidden document:', doc.name);
          return false;
        }
        
        // Explicitly check for draft flag
        if (doc.metadata.isDraft === true) {
          console.log('Filtering out draft document:', doc.name);
          return false;
        }
      }
      
      // For non-LOA documents, keep them all
      if (doc.category !== 'LOA') {
        return true;
      }
      
      // For LOA documents, we want to KEEP signed documents and filter out unsigned ones
      
      // KEEP documents explicitly marked as signed in metadata
      if (doc.metadata && doc.metadata.isSigned === true) {
        console.log('Keeping document with isSigned=true:', doc.name);
        return true;
      }
      
      // KEEP documents with 'signed' in the name
      if (doc.name && doc.name.toLowerCase().includes('signed')) {
        console.log('Keeping document with "signed" in name:', doc.name);
        return true;
      }
      
      // KEEP documents with 'signed' in the s3Key
      if (doc.s3Key && doc.s3Key.toLowerCase().includes('signed')) {
        console.log('Keeping document with "signed" in s3Key:', doc.s3Key);
        return true;
      }
      
      // Filter out documents explicitly marked as unsigned
      if (doc.metadata && doc.metadata.documentType === 'unsigned_loa') {
        console.log('Filtering out unsigned LOA document:', doc.name);
        return false;
      }
      
      // Filter out documents with unsigned indicators in s3Key
      if (doc.s3Key && (doc.s3Key.includes('LOA_') || doc.s3Key.includes('/unsigned/'))) {
        console.log('Filtering out document with unsigned indicators in s3Key:', doc.s3Key);
        return false;
      }
      
      // For any other LOA document, let's keep it by default to avoid filtering out valid documents
      console.log('Keeping other LOA document by default:', doc.name);
      return true;
    });
    
    // Log filtered documents for debugging
    console.log('Filtered documents:', filtered);
    
    // Update filtered documents state
    setFilteredDocuments(filtered);
  }, [documents]);

  const deleteDocument = async (documentId: string) => {
    try {
      let url = `/api/clients/${clientId}/documents/${documentId}`;
      if (propertyId && documentType === 'property') {
        url = `/api/clients/${clientId}/properties/${propertyId}/documents/${documentId}`;
      }
      await axios.delete(url);
    } catch (error) {
      
      throw error;
    }
  };

  const handleDeleteClick = (document: Document) => {
    setSelectedDocument(document);
    setDeleteDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!selectedDocument) return;

    try {
      setLocalLoading(true);
      if (onDeleteDocument) {
        onDeleteDocument(selectedDocument._id);
      } else {
        await deleteDocument(selectedDocument._id);
        toast.success('Document deleted successfully');
        if (onDocumentDeleted) {
          onDocumentDeleted();
        }
      }
    } catch (error) {
      
      toast.error('Failed to delete document');
    } finally {
      setLocalLoading(false);
      setDeleteDialogOpen(false);
      setSelectedDocument(null);
    }
  };

  const handleView = async (doc: Document) => {
    if (onViewDocument) {
      onViewDocument(doc);
    }
  };



  const handleDownload = async (doc: Document) => {
    try {
      setLocalLoading(true);
      // Use the correct API endpoint path without /api prefix since axiosConfig already includes it
      const downloadUrl = propertyId
        ? `/clients/${clientId}/properties/${propertyId}/documents/${doc._id}/download`
        : `/clients/${clientId}/documents/${doc._id}/download`;
      
      // Get the presigned URL from the server
      const response = await axios.get(downloadUrl);
      
      if (response.data && response.data.success && response.data.url) {
        // Since we can't rely on the download attribute with S3 URLs (cross-origin issues),
        // we'll fetch the file content directly and create a local download
        try {
          // Fetch the file content with blob response type
          const fileResponse = await fetch(response.data.url, {
            method: 'GET',
            mode: 'cors', // This is important for cross-origin requests
          });
          
          if (!fileResponse.ok) {
            throw new Error(`HTTP error! status: ${fileResponse.status}`);
          }
          
          // Get the blob from the response
          const blob = await fileResponse.blob();
          
          // Create a local URL for the blob
          const blobUrl = window.URL.createObjectURL(blob);
          
          // Create a download link with the blob URL
          const link = document.createElement('a');
          link.href = blobUrl;
          link.download = doc.name || 'document'; // Force download with specific filename
          link.style.display = 'none';
          document.body.appendChild(link);
          
          // Trigger the download
          link.click();
          
          // Clean up
          setTimeout(() => {
            document.body.removeChild(link);
            window.URL.revokeObjectURL(blobUrl);
          }, 100);
        } catch (fetchError) {
          console.error('Error fetching file:', fetchError);
          
          // Fallback: If fetch fails, try opening in a new tab
          // This at least gives the user a chance to download manually
          window.open(response.data.url, '_blank');
          toast.success('Please use your browser\'s save option to download the file');
        }
      } else {
        throw new Error('Invalid response format from server');
      }
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download document');
    } finally {
      setLocalLoading(false);
    }
  };

  if (loading || localLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Paper sx={{ p: 3, textAlign: 'center' }}>
        <Typography color="error" gutterBottom>
          {error}
        </Typography>
        <Button variant="outlined" onClick={onDocumentDeleted}>
          Retry
        </Button>
      </Paper>
    );
  }





  // Render the component based on the current state
  if (loading || localLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Paper sx={{ p: 3, textAlign: 'center' }}>
        <Typography color="error" gutterBottom>
          {error}
        </Typography>
      </Paper>
    );
  }

  if (filteredDocuments.length === 0) {
    return (
      <Paper sx={{ p: 3, textAlign: 'center' }}>
        <Typography color="textSecondary" gutterBottom>
          No documents found
        </Typography>
        <Typography variant="body2" color="textSecondary" paragraph>
          Upload documents to keep track of important files for this {documentType}.
        </Typography>
      </Paper>
    );
  }

  return (
    <Box>
      <Paper>
        <List>
          {filteredDocuments.map((document) => (
            <ListItem key={document._id}>
              <ListItemIcon>
                {getFileIcon(document.type)}
              </ListItemIcon>
              <ListItemText
                primary={document.name}
                secondary={
                  <>
                    <Typography component="span" variant="body2" color="textSecondary">
                      {formatFileSize(document.size)} • Uploaded {document.createdAt ? formatDate(document.createdAt) : 'N/A'}
                    </Typography>
                    <br />
                    <Chip
                      size="small"
                      label={document.type}
                      sx={{ mr: 1 }}
                    />
                  </>
                }
              />
              <ListItemSecondaryAction>
                <IconButton
                  edge="end"
                  aria-label="download"
                  onClick={() => handleDownload(document)}
                  sx={{ mr: 1 }}
                >
                  <DownloadIcon />
                </IconButton>
                {onViewDocument && (
                  <IconButton
                    edge="end"
                    aria-label="view"
                    onClick={() => onViewDocument(document)}
                    sx={{ mr: 1 }}
                  >
                    <VisibilityIcon />
                  </IconButton>
                )}
                <IconButton
                  edge="end"
                  aria-label="delete"
                  onClick={() => handleDeleteClick(document)}
                >
                  <DeleteIcon />
                </IconButton>
              </ListItemSecondaryAction>
            </ListItem>
          ))}
        </List>
      </Paper>
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this document? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleDelete}
            color="error"
            variant="contained"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DocumentList;
